<template>
  <div v-if="status !== 'pending'" class="otp-delivery">
    <!-- Письмо отклонено: ждать нечего, экран предлагает другой путь. -->
    <template v-if="status === 'rejected'">
      <h3 class="otp-delivery__title">{{ t('auth.otp.delivery.rejectedTitle') }}</h3>
      <p class="otp-delivery__text">{{ t('auth.otp.delivery.rejectedText') }}</p>
    </template>

    <p v-else-if="status === 'delivered'" class="otp-delivery__note" data-test="otp-delivered">
      {{ t('auth.otp.delivery.delivered') }}
    </p>

    <!-- Задерживается: поле кода остаётся, подсказка мягкая. -->
    <a
      v-else-if="!expanded"
      href="#"
      class="otp-delivery__hint"
      data-test="otp-delayed-hint"
      @click.prevent="expanded = true"
    >
      {{ t('auth.otp.delivery.delayedHint') }}
    </a>

    <p v-if="status === 'confirmed'" class="otp-delivery__note" data-test="otp-confirmed">
      {{ t('auth.otp.delivery.confirmed') }}
    </p>

    <div v-else-if="showChannels" class="otp-delivery__channels">
      <!-- IGRA-388: ссылка на бота уже получена — дальше человек уходит в него. -->
      <template v-if="telegramLink">
        <a
          class="otp-delivery__btn otp-delivery__btn--primary"
          :href="telegramLink"
          target="_blank"
          rel="noopener"
          data-test="otp-telegram-link"
        >
          {{ t('auth.otp.delivery.telegramOpen') }}
        </a>
        <p class="otp-delivery__note" data-test="otp-telegram-hint">{{ t('auth.otp.delivery.telegramHint') }}</p>
      </template>

      <!-- IGRA-389: номер и согласие на его обработку. Не <form>: блок стоит
           внутри формы ввода кода, а форма в форме — невалидная верстка. -->
      <div v-else-if="phoneStep === 'form'" class="otp-delivery__phone">
        <label class="otp-delivery__label" for="otp-phone">{{ t('auth.otp.delivery.phoneLabel') }}</label>
        <input
          id="otp-phone"
          v-model="phone"
          type="tel"
          inputmode="tel"
          autocomplete="tel"
          class="otp-delivery__input ym-disable-keys"
          placeholder="+7 999 123-45-67"
          data-test="otp-phone-input"
        />

        <label class="otp-delivery__consent">
          <input v-model="consent" type="checkbox" data-test="otp-phone-consent" />
          <span>{{ t('auth.otp.delivery.phoneConsent') }}</span>
        </label>

        <!-- Не primary: главная кнопка экрана — «Продолжить» у формы кода,
             две сплошные кнопки подряд спорят друг с другом. -->
        <button
          type="button"
          class="otp-delivery__btn otp-delivery__btn--outline"
          data-test="otp-phone-submit"
          :disabled="isRequesting || !consent"
          @click="startCall"
        >
          {{ t('auth.otp.delivery.phoneSubmit') }}
        </button>
      </div>

      <!-- IGRA-389: номер выдан — звонит сам человек. -->
      <div v-else-if="phoneStep === 'calling'" class="otp-delivery__phone">
        <p class="otp-delivery__text" data-test="otp-call-phone">
          {{ t('auth.otp.delivery.phoneCallTitle') }} <strong>{{ callPhone }}</strong>
        </p>
        <p class="otp-delivery__note">{{ t('auth.otp.delivery.phoneCallHint') }}</p>

        <a class="otp-delivery__btn otp-delivery__btn--primary" :href="`tel:${callPhone}`" data-test="otp-call-link">
          {{ t('auth.otp.delivery.phoneCallAction') }}
        </a>

        <p v-if="callState === 'pending'" class="otp-delivery__note" data-test="otp-call-timer">
          {{ t('auth.otp.delivery.phoneCallTimer', { seconds: secondsLeft }) }}
        </p>

        <template v-else-if="callState === 'confirmed'">
          <p class="otp-delivery__note" data-test="otp-call-confirmed">{{ t('auth.otp.delivery.confirmed') }}</p>
        </template>

        <template v-else>
          <p class="otp-delivery__note" data-test="otp-call-problem">{{ callProblem }}</p>
          <button
            type="button"
            class="otp-delivery__btn otp-delivery__btn--outline"
            data-test="otp-call-retry"
            @click="phoneStep = 'form'"
          >
            {{ t('auth.otp.delivery.phoneRetry') }}
          </button>
        </template>
      </div>

      <button
        v-for="channel in channels"
        v-else
        :key="channel"
        type="button"
        class="otp-delivery__btn"
        :class="channel === 'telegram' ? 'otp-delivery__btn--primary' : 'otp-delivery__btn--outline'"
        :data-test="`otp-channel-${channel}`"
        :disabled="isRequesting"
        @click="choose(channel)"
      >
        {{ t(CHANNEL_LABELS[channel]) }}
      </button>

      <p v-if="notice" class="otp-delivery__note" data-test="otp-channel-notice">{{ notice }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * IGRA-387 — что видит человек, когда письмо с кодом не дошло.
 *
 * Состояния приходят с бэкенда (IGRA-386) и здесь только показываются:
 *  - `delivered` — короткая пометка «письмо доставлено»;
 *  - `delayed`   — мягкая подсказка, поле кода остаётся на месте;
 *  - `rejected`  — вместо формы блок с объяснением и списком каналов.
 *
 * Порядок кнопок задаёт бэкенд и не сортируется здесь: телеграм первым,
 * «другой email» последним. Названия провайдера письма в UI нет.
 *
 * IGRA-388: «Получить код в Telegram» просит у бэкенда одноразовую ссылку на
 * бота. Сессию этот экран не создаёт: человек подтверждает вход в боте, а
 * опрос статуса приносит сюда `confirmed` — вход заканчивает родительская
 * модалка тем же путём, что и верный код.
 *
 * IGRA-389: «Подтвердить звонком» спрашивает номер и согласие на его
 * обработку, получает у сервиса номер, на который звонит САМ человек, и
 * опрашивает судьбу звонка — вебхуков у сервиса нет. Подтверждение приходит
 * тем же путём, что и из бота.
 */
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ProfileApi,
  type OtpDeliveryChannel,
  type OtpDeliveryStatus,
  type OtpPhoneCallState,
} from '@/modules/profile/api/profileApi'
import { trackEvent } from '@/shared/analytics'

const props = defineProps<{
  status: OtpDeliveryStatus
  channels: OtpDeliveryChannel[]
  /** Адрес, на который уходил код: по нему бэкенд находит сессию. */
  email: string
}>()

const emit = defineEmits<{
  (e: 'change-email'): void
  /** Человек ушёл в другой канал — родителю пора ждать подтверждения. */
  (e: 'await-confirmation'): void
}>()

const { t } = useI18n()

const CHANNEL_LABELS: Record<OtpDeliveryChannel, string> = {
  telegram: 'auth.otp.delivery.channelTelegram',
  phone: 'auth.otp.delivery.channelPhone',
  email: 'auth.otp.delivery.channelEmail',
}

const expanded = ref(false)
const notice = ref('')
const telegramLink = ref('')
const isRequesting = ref(false)

/** Шаг канала звонка: пусто → кнопки каналов, form → номер, calling → звонок. */
const phoneStep = ref<'' | 'form' | 'calling'>('')
const phone = ref('')
const consent = ref(false)
const callPhone = ref('')
const callState = ref<OtpPhoneCallState>('pending')
const secondsLeft = ref(0)

/** Опрос судьбы звонка и обратный отсчёт до его истечения. */
let pollTimer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null

const stopCallTimers = () => {
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  if (tickTimer !== null) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}

onBeforeUnmount(stopCallTimers)

const showChannels = computed(() => props.status === 'rejected' || expanded.value)

/** Что показать человеку, когда звонка не случилось. */
const callProblem = computed(() => {
  if (callState.value === 'mismatch') return t('auth.otp.delivery.phoneMismatch')
  if (callState.value === 'unavailable') return t('auth.otp.delivery.phoneUnavailable')

  return t('auth.otp.delivery.phoneExpired')
})

/**
 * Звонок заводит бэкенд: он же проверяет номер и согласие. Пустой ответ
 * (сервис недоступен) не ломает экран — остаются телеграм и другой email.
 */
const startCall = async () => {
  // Без согласия бэк вернёт PHONE_CONSENT_REQUIRED — не гоняем заведомый брак.
  if (isRequesting.value || !consent.value) return
  isRequesting.value = true
  notice.value = ''

  try {
    const result = await ProfileApi.startPhoneVerification(props.email, phone.value, consent.value)

    if (result.state === 'unavailable' || !result.call_phone) {
      notice.value = t('auth.otp.delivery.phoneUnavailable')
      return
    }

    callPhone.value = result.call_phone
    callState.value = 'pending'
    secondsLeft.value = result.expires_in ?? 60
    phoneStep.value = 'calling'
    emit('await-confirmation')
    watchCall()
  } catch (error) {
    console.error('[igra] не удалось завести звонок', error)
    notice.value = t('auth.otp.delivery.phoneFormError')
  } finally {
    isRequesting.value = false
  }
}

/** Вебхуков у сервиса нет — спрашиваем сами, раз в три секунды. */
const watchCall = () => {
  stopCallTimers()

  tickTimer = setInterval(() => {
    secondsLeft.value = Math.max(0, secondsLeft.value - 1)
  }, 1000)

  pollTimer = setInterval(async () => {
    try {
      const result = await ProfileApi.getPhoneVerificationStatus(props.email)
      if (result.state === 'pending' || result.state === 'none') return

      callState.value = result.state
      stopCallTimers()
    } catch (error) {
      console.error('[igra] не удалось узнать судьбу звонка', error)
      callState.value = 'unavailable'
      stopCallTimers()
    }
  }, 3000)
}

const choose = async (channel: OtpDeliveryChannel) => {
  trackEvent('otp_delivery_status', { status: props.status, channel_chosen: channel })

  if (channel === 'email') {
    emit('change-email')
    return
  }

  if (channel === 'phone') {
    phoneStep.value = 'form'
    return
  }

  if (isRequesting.value) return
  isRequesting.value = true
  notice.value = ''

  try {
    const result = await ProfileApi.requestLoginCodeTelegram(props.email)
    telegramLink.value = result.link
    emit('await-confirmation')
    // Телеграм на телефоне откроется сам; на десктопе человек нажмёт кнопку.
    window.open(result.link, '_blank', 'noopener')
  } catch (error) {
    // Точная причина (нет сессии, лимит ссылок) человеку ничего не объясняет:
    // ему нужен работающий путь в кабинет, а не код ошибки.
    console.error('[igra] не удалось получить ссылку на бота', error)
    notice.value = t('auth.otp.delivery.telegramError')
  } finally {
    isRequesting.value = false
  }
}
</script>

<style scoped lang="scss">
.otp-delivery {
  /* Блок вклинивается между полем кода и кнопкой «Продолжить»: без нижнего
     отступа его кнопка слипается с ней в одну сплошную стопку. */
  margin-top: 20px;
  margin-bottom: 20px;
}

.otp-delivery__title {
  font-size: var(--p-text-18);
  font-weight: var(--ds-weight-bold);
  text-align: center;
  color: var(--ds-text);
  margin-bottom: 8px;
}

.otp-delivery__text {
  text-align: center;
  color: var(--ds-text-secondary);
  line-height: 1.5;
  margin-bottom: 20px;
}

.otp-delivery__note {
  text-align: center;
  color: var(--ds-text-secondary);
  font-size: var(--ds-text-size-body);
  margin-top: 12px;
}

.otp-delivery__hint {
  display: block;
  text-align: center;
  color: var(--ds-text);
  font-size: var(--ds-text-size-body);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.otp-delivery__channels {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.otp-delivery__btn {
  width: 100%;
  display: block;
  padding: 14px;
  text-align: center;
  text-decoration: none;
  border-radius: var(--ds-radius-control);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body-lg);
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.otp-delivery__btn--primary {
  background: var(--ds-text);
  color: var(--ds-surface);

  &:hover {
    background: var(--ds-text-secondary);
  }
}

.otp-delivery__phone {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.otp-delivery__label {
  color: var(--ds-text-secondary);
  font-size: var(--ds-text-size-body);
}

.otp-delivery__input {
  width: 100%;
  padding: 14px;
  border-radius: var(--ds-radius-control);
  border: 1px solid var(--ds-border);
  background: transparent;
  color: var(--ds-text);
  font-size: var(--ds-text-size-body-lg);
}

.otp-delivery__consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--ds-text-secondary);
  font-size: var(--ds-text-size-body);
  line-height: 1.45;
  cursor: pointer;

  input {
    flex: none;
    width: 18px;
    height: 18px;
    margin-top: 1px;
    /* Иначе на тёмной модалке стоит серый системный квадрат. */
    accent-color: var(--ds-accent);
  }
}

.otp-delivery__btn--outline {
  background: transparent;
  border: 1px solid var(--ds-border);
  color: var(--ds-text);

  &:hover {
    background: var(--ds-surface-raised);
  }
}
</style>
